import { getRouteApi } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import EditIcon from '@/assets/icons/edit.svg?react'
import Alert from '@/components/Alert/Alert'
import DataBlock from '@/components/DataBlock/DataBlock'
import Tag from '@/components/Tag/Tag'
import type { Action } from '@/components/common/BasicLayout/Actions'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import useAuthStore from '@/store/useAuthStore'
import { formatDate } from '@/utils/format-date'

import styles from './SchoolYearDetailsView.module.scss'
import { schoolYearStatusToTagMap } from '../constants/school-year-status-to-tag-map'
import { useSchoolYear } from '../queries/useSchoolYear'
import type { SchoolYear } from '../types'

const routeApi = getRouteApi('/_auth/school-settings/years/$yearId/')

const SchoolYearDetailsView = () => {
  const { t } = useTranslation('schoolSettings')

  const { user } = useAuthStore()

  const { yearId } = routeApi.useParams()
  const { data: year } = useSchoolYear(yearId)

  if (!year) return null

  const schoolYearInfo = [
    { label: t('label.name'), value: year.name },
    {
      label: t('label.start-date'),
      value: formatDate(year.startDate)
    },
    {
      label: t('label.end-date'),
      value: formatDate(year.endDate)
    }
  ]

  const firstSemesterInfo = [
    {
      label: t('label.start-date'),
      value: formatDate(year.firstSemester.startDate)
    },
    {
      label: t('label.end-date'),
      value: formatDate(year.firstSemester.endDate)
    }
  ]
  const secondSemesterInfo = [
    {
      label: t('label.start-date'),
      value: formatDate(year.secondSemester.startDate)
    },
    {
      label: t('label.end-date'),
      value: formatDate(year.secondSemester.endDate)
    }
  ]

  const actions: Action[] = [
    ...(user?.isSuperAdmin
      ? ([
          {
            text: t('button.edit'),
            icon: <EditIcon />,
            asLink: true,
            to: '/school-settings/years/$yearId/edit',
            params: {
              yearId
            },
            hidden: year.status !== 'future'
          }
        ] satisfies Action[])
      : [])
  ]
  return (
    <BasicLayout
      breadcrumbs={[
        {
          title: t('header.school-settings'),
          to: '/school-settings'
        },
        {
          title: year.name ?? ''
        }
      ]}
      actions={actions}
      subheader={t('header.school-year-details')}
      header={<SchoolYearDetailsHeader year={year} />}
      moduleName={t('header.administration', { ns: 'common' })}
    >
      {year.status === 'archived' ? (
        <Alert
          message={t('help.archived-school-year')}
          className={styles.alert}
          variant="warning"
        />
      ) : null}

      <section className={styles.section}>
        <h2 className={styles.sectionHeader}>{t('header.details')}</h2>

        <div className={styles.content}>
          <div className={styles.dataBlocksWrapper}>
            <DataBlock
              header={t('header.school-year')}
              infoArray={schoolYearInfo}
            />
          </div>

          <div className={styles.dataBlocksWrapper}>
            <DataBlock
              header={t('header.semester-1')}
              infoArray={firstSemesterInfo}
            />
            <DataBlock
              header={t('header.semester-2')}
              infoArray={secondSemesterInfo}
            />
          </div>
        </div>
      </section>
    </BasicLayout>
  )
}

const SchoolYearDetailsHeader = ({ year }: { year: SchoolYear }) => {
  const { t } = useTranslation('schoolSettings')

  return (
    <div className={styles.headerWrapper}>
      <h1 className={styles.header}>{year?.name}</h1>
      <Tag {...schoolYearStatusToTagMap(t)[year?.status]} />
    </div>
  )
}
export default SchoolYearDetailsView
